import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { perfectlyImperfectColors } from 'shared/misc/theme/colors';
import { cn } from 'web2/app/utils/cn';

export const StyledStar = ({
  width,
  height,
  fill,
}: {
  width: number;
  height: number;
  fill: string;
}) => (
  <div
    style={{
      maskImage: "url('/static/spray-star-yellow-small.png')",
      maskSize: 'contain',
      maskRepeat: 'no-repeat',
      backgroundColor: fill,
      width: `calc(${width}px * var(--star-size-multiplier))`,
      height: `calc(${height}px * var(--star-size-multiplier))`,
    }}
    className="alex-here inline-block"
  ></div>
);

interface Props {
  children: ReactNode;
  skipStars?: boolean;
  size?: number;
  offsetEdgeAmt?: number;
  className?: string;
}

const LARGE = 110;
const MEDIUM = 100;

export const STAR_SIZES = [MEDIUM, MEDIUM, MEDIUM, 125, 125, LARGE, LARGE];

const SIDES: string[] = [
  'top-left',
  'middle-left',
  'bottom-left',
  'top-right',
  'middle-right',
  'bottom-right',
  'bottom-middle',
  'top-middle',
];

function StarContainer({
  children,
  size: propSize,
  offsetEdgeAmt: propOffsetEdgeAmt,
  skipStars,
  className,
  ...props
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isMainImageLoaded, setIsMainImageLoaded] = useState<boolean>(false);
  const containerNode = ref?.current || null;

  interface StarInfo {
    width: number;
    height: number;
    rotate: number;
    style: React.CSSProperties;
    side: string;
  }

  function genStarInfo(side: string, size: number): StarInfo {
    const halfSize = `${size / 2}px`;

    const style: React.CSSProperties = {
      width: `calc(${size}px * var(--star-size-multiplier))`,
      height: `calc(${size}px * var(--star-size-multiplier))`,
      position: 'absolute',
      pointerEvents: 'none',
      userSelect: 'none',
    };

    // Assign exact positioning
    switch (side) {
      case 'top-left':
        style.top = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.left = `calc(-${halfSize} * var(--star-size-multiplier))`;
        break;
      case 'middle-left':
        style.top = '50%';
        style.left = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.transform = 'translateY(-50%)';
        break;
      case 'bottom-left':
        style.bottom = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.left = `calc(-${halfSize} * var(--star-size-multiplier))`;
        break;
      case 'top-middle':
        style.top = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.left = '50%';
        style.transform = 'translateX(-50%)';
        break;
      case 'bottom-middle':
        style.bottom = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.left = '50%';
        style.transform = 'translateX(-50%)';
        break;
      case 'top-right':
        style.top = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.right = `calc(-${halfSize} * var(--star-size-multiplier))`;
        break;
      case 'middle-right':
        style.top = '50%';
        style.right = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.transform = 'translateY(-50%)';
        break;
      case 'bottom-right':
        style.bottom = `calc(-${halfSize} * var(--star-size-multiplier))`;
        style.right = `calc(-${halfSize} * var(--star-size-multiplier))`;
        break;
    }

    return { width: size, height: size, rotate: 0, side, style };
  }

  const stars = useMemo(() => {
    if (!containerNode || !isMainImageLoaded) return [];
    return _.times(SIDES.length, (n) => {
      const size = propSize || (_.sample(STAR_SIZES) as number);
      const side = SIDES[n];
      return genStarInfo(side, size);
    });
  }, [containerNode, isMainImageLoaded, propSize]);

  return skipStars ? (
    <div className={className}>{children}</div>
  ) : (
    <div
      className={cn('relative block overflow-visible', className)}
      style={{ width: 'fit-content', height: 'fit-content', display: 'inline-block' }}
      ref={ref}
      onLoad={(e) => {
        const target = e.target as HTMLElement;
        const parentElement = target?.parentNode as HTMLElement;
        const isMainImage = parentElement?.dataset?.imageType === 'star' ? false : true;
        if (isMainImage) {
          setIsMainImageLoaded(true);
        }
      }}
      {...props}
    >
      {stars.map(({ width, height, side, style }) => (
        <div key={side} style={style} data-image-type="star">
          <StyledStar fill="var(--theme-highlight)" width={width} height={height} />
        </div>
      ))}
      {children}
    </div>
  );
}

export default StarContainer;
